import React, { useMemo } from "react";
import { cn } from "@/lib/utils";

/** Smooth area chart (Catmull-Rom -> Bezier) with animated draw-in. Values: [{date,value}] */
export function AreaTrend({ data = [], className, height = 180, stroke = "hsl(10 76% 61%)", id = "trend", labels = true, formatX, formatY }) {
  const W = 800;
  const H = 240;
  const padX = 8;
  const padTop = 22;
  const padBottom = labels ? 34 : 10;
  const pts = useMemo(() => {
    if (!data.length) return [];
    const vals = data.map((d) => d.value);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const span = Math.max(1, max - min);
    const lo = min - span * 0.25;
    const hi = max + span * 0.12;
    return data.map((d, i) => ({
      x: padX + (i / Math.max(1, data.length - 1)) * (W - padX * 2),
      y: padTop + (1 - (d.value - lo) / (hi - lo)) * (H - padTop - padBottom),
      d,
    }));
  }, [data, padBottom]);

  const path = useMemo(() => {
    if (pts.length < 2) return "";
    let d = `M ${pts[0].x} ${pts[0].y}`;
    for (let i = 0; i < pts.length - 1; i += 1) {
      const p0 = pts[i - 1] || pts[i];
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const p3 = pts[i + 2] || p2;
      const c1x = p1.x + (p2.x - p0.x) / 6;
      const c1y = p1.y + (p2.y - p0.y) / 6;
      const c2x = p2.x - (p3.x - p1.x) / 6;
      const c2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C ${c1x} ${c1y}, ${c2x} ${c2y}, ${p2.x} ${p2.y}`;
    }
    return d;
  }, [pts]);

  if (!pts.length) return null;
  const last = pts[pts.length - 1];
  const area = `${path} L ${last.x} ${H - padBottom} L ${pts[0].x} ${H - padBottom} Z`;
  const xLabels = labels ? [0, Math.floor((pts.length - 1) / 3), Math.floor(((pts.length - 1) * 2) / 3), pts.length - 1].map((i) => pts[i]) : [];
  const gridYs = [0.25, 0.5, 0.75].map((f) => padTop + f * (H - padTop - padBottom));

  return (
    <div className={cn("relative w-full", className)} style={{ height }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-full w-full overflow-visible" aria-hidden="true">
        <defs>
          <linearGradient id={`${id}-fill`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.22" />
            <stop offset="70%" stopColor={stroke} stopOpacity="0.04" />
            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
          </linearGradient>
          <linearGradient id={`${id}-line`} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor={stroke} stopOpacity="0.35" />
            <stop offset="60%" stopColor={stroke} stopOpacity="0.9" />
            <stop offset="100%" stopColor={stroke} stopOpacity="1" />
          </linearGradient>
          <clipPath id={`${id}-clip`}>
            <rect x="0" y="0" width={W} height={H} className="chart-reveal" />
          </clipPath>
        </defs>
        {gridYs.map((y) => <line key={y} x1={padX} x2={W - padX} y1={y} y2={y} stroke="hsl(30 12% 18% / 0.06)" strokeWidth="1" vectorEffect="non-scaling-stroke" />)}
        <g clipPath={`url(#${id}-clip)`}>
          <path d={area} fill={`url(#${id}-fill)`} />
          <path d={path} fill="none" stroke={`url(#${id}-line)`} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
        </g>
      </svg>
      {/* End marker (HTML so it stays circular under preserveAspectRatio=none) */}
      <span className="pointer-events-none absolute flex h-3 w-3 -translate-x-1/2 -translate-y-1/2 items-center justify-center" style={{ left: `${(last.x / W) * 100}%`, top: `${(last.y / H) * 100}%` }}>
        <span className="absolute h-3 w-3 rounded-full" style={{ backgroundColor: stroke, opacity: 0.25 }} />
        <span className="absolute h-3 w-3 animate-ping rounded-full" style={{ backgroundColor: stroke, opacity: 0.25, animationDuration: "2.4s" }} />
        <span className="h-2 w-2 rounded-full ring-2 ring-white" style={{ backgroundColor: stroke }} />
      </span>
      {labels && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 flex justify-between px-1 text-[10.5px] tabular text-muted-foreground/80">
          {xLabels.map((p, i) => <span key={i}>{formatX ? formatX(p.d) : p.d.date}</span>)}
        </div>
      )}
      {formatY && (
        <div className="pointer-events-none absolute right-1 top-0 text-[10.5px] tabular text-muted-foreground/70">{formatY(Math.max(...data.map((d) => d.value)))}</div>
      )}
    </div>
  );
}
